'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function BotoksFiyatlariNedenFarkliPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/botoks-fiyatlari-neden-farkli';
  const pageTitle = 'Botoks Fiyatları Neden Bazı Muayenehanelerde Çok Ucuzken Bazı Muayenehanelerde Çok Pahalı?';
  const pageDescription = 'Botoks fiyatları neden bu kadar farklı? Ucuz ve pahalı botoks arasındaki fark, kullanılan ürün kalitesi, hekim deneyimi ve klinik koşullarıyla ilgilidir. Detaylı açıklama burada.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Botoks Fiyatları Neden Farklı?', href: '/tr/blog/botoks-fiyatlari-neden-farkli' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Botoks fiyat farklılıkları ve kalite değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Botoks Fiyatları
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Botoks Fiyatları Neden Bazı Muayenehanelerde Çok Ucuzken Bazı Muayenehanelerde Çok Pahalı?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>21 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Botoks, yüz kırışıklıklarını azaltmak ve gençleşmiş bir görünüm sağlamak için en sık tercih edilen estetik işlemlerden biridir. Ancak fiyatlar muayenehaneden muayenehaneye ciddi farklılık gösterir. Bazı yerlerde çok uygun fiyatlarla sunulurken, bazı muayenehanelerde fiyatlar oldukça yüksektir. Peki bu farkın sebebi nedir? Cevap, ürün kalitesi, uygulama güvenliği ve uzmanlık düzeyi gibi birçok faktörde gizlidir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Kullanılan Ürünün Orijinalliği
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks fiyatını en çok etkileyen unsur, ürünün orijinal olup olmamasıdır. Türkiye'de Sağlık Bakanlığı onaylı orijinal botulinum toksin markaları sınırlıdır. Ancak piyasada ruhsatsız, internet üzerinden getirilen veya "muadil" adıyla satılan ürünler bulunmaktadır.
              </p>
              
              <div className="bg-green-50 border-l-4 border-green-400 p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-900 mb-3">➡️ Orijinal botoks ürünleri:</h3>
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Laboratuvar koşullarında üretilir</li>
                  <li>Her seri için kalite sertifikası taşır</li>
                  <li>Saklama ve taşıma koşulları belirlenmiştir</li>
                </ul>
                <p className="text-green-800 mt-3">
                  Ucuza yapılan işlemlerde ise bu standartlar çoğu zaman karşılanmaz. Bu da hem kısa süreli etki, hem de alerjik veya nörolojik komplikasyon riskini artırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Hekimin Uzmanlığı ve Deneyimi
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks uygulaması yalnızca medikal estetik alanında eğitim almış hekimler tarafından yapılmalıdır. Bazı düşük fiyatlı uygulamalarda, işlemler tecrübesiz kişiler tarafından yapılmakta veya hekim kontrolü bulunmamaktadır. Bu durum sadece estetik sonuçları değil, sağlık güvenliğini de doğrudan etkiler.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Deneyimli bir doktor:</h3>
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li>Kas anatomisini doğru değerlendirir</li>
                  <li>Doz ayarını kişiye özel yapar</li>
                  <li>Komplikasyon riskini minimize eder</li>
                </ul>
                <p className="text-blue-800 mt-3 font-medium">
                  Bu da doğal olarak fiyat farkına yansır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Muayenehane Ortamı ve Hijyen Standartları
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Steril, lisanslı ve denetlenen muayenehanelerde uygulama maliyeti yüksektir. Ancak bu maliyet, hastanın güvenliğini garanti altına alır. Sterilizasyon, tek kullanımlık enjektörler, uygun saklama koşulları gibi unsurlar profesyonel bir muayenehanede zorunludur.
              </p>
              
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-800 font-medium">
                  Ucuz işlemlerde bu kriterler çoğu zaman göz ardı edilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Doz, Bölge Sayısı ve Ürün Miktarı
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoksun etkisi, uygulanan bölgeye ve kas gücüne göre değişir. Bazı muayenehaneler düşük fiyat sunabilmek için daha az doz veya seyreltilmiş ürün kullanır. Bu da:
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-red-800 space-y-2">
                  <li>Etkinin kısa sürmesine</li>
                  <li>Asimetrik görünümlere</li>
                  <li>Tekrarlama ihtiyacına yol açar</li>
                </ul>
                <p className="text-red-800 mt-3">
                  Kaliteli uygulamada ise ürün tam doz ve doğru derinlikte enjekte edilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Fiyatın Kaliteyle İlişkisi
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Botoks bir "pazar ürünü" değil, tıbbi bir uygulamadır. Bu nedenle sadece fiyat üzerinden tercih yapmak ciddi sağlık riskleri oluşturabilir. Uzman hekimlerin uyguladığı, belgeli ürünlerin kullanıldığı merkezlerde fiyatlar daha yüksek olsa da sonuç hem güvenli hem de uzun ömürlüdür.
              </p>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Botoks fiyat farklılıkları değerlendirmesi"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Ucuz botoks tehlikeli midir?</h3>
                  <p className="text-gray-700">Evet, orijinal olmayan ürünler ve yanlış doz uygulamaları kas felci veya alerjik reaksiyonlara neden olabilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Botoks fiyatı neye göre belirlenir?</h3>
                  <p className="text-gray-700">Kullanılan ürün markası, doz miktarı, uygulanan bölge sayısı ve hekim deneyimi fiyatı belirler.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Orijinal botoksu nasıl anlarım?</h3>
                  <p className="text-gray-700">Ürün ambalajında barkod, seri numarası ve Sağlık Bakanlığı onay etiketi bulunur. Ayrıca hekim, kullanılan ürünün kutusunu göstermekle yükümlüdür.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Çok ucuz fiyatlı botoksun riski nedir?</h3>
                  <p className="text-gray-700">Etki süresi kısa olur, asimetri veya kas zayıflığı gelişebilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Her pahalı botoks iyi midir?</h3>
                  <p className="text-gray-700">Hayır, önemli olan ürünün orijinal olması ve işlemin uzman hekim tarafından yapılmasıdır.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>American Society for Dermatologic Surgery (ASDS) – Botulinum Toxin Application Standards</li>
                <li>Türk Dermatoloji Derneği – Botoks Uygulama Rehberi</li>
                <li>FDA – Botulinum Toxin Safety Guidelines</li>
                <li>Harvard Health Publishing – "How to Choose Safe Cosmetic Injections"</li>
                <li>Mayo Clinic – "Botox: Uses and Side Effects"</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botoks</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botoksfiyatları</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#orijinalbotoks</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#muayenehaneseçimi</span>
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">#botulinumtoxin</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Güvenli Botoks Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">Orijinal ürünler ve uzman hekim deneyimiyle güvenli botoks uygulaması için hemen iletişime geçin.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Güvenli botoks uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/botoks-fiyatlari-2026"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Fiyatları 2026</h4>
              <p className="text-gray-600 text-sm">Güncel botoks fiyatları ve nelere göre değiştiği hakkında detaylı bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/noroloji-doktorlari-neden-botoks-onermez"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Nöroloji Doktorları Neden Botoks Önermez?</h4>
              <p className="text-gray-600 text-sm">Nöroloji uzmanlarının botoks konusundaki görüşleri ve risk faktörleri.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

